import { exists } from "@@/utilities/exists";
import { unique } from "@@/utilities/unique";
import type { Resource } from "../resources/resource";
import type {
    ResourceGraph} from "../resources/resource-graph";
import {
    isNotResourceGraph,
    isResourceGraph
} from "../resources/resource-graph";
import type {
    ResourceGroup} from "../resources/resource-group";
import {
    isNotResourceGroup,
    isResourceGroup
} from "../resources/resource-group";
import type { ResourceId } from "../resources/resource-id";

/**
 * Recursively searches for a subgraph within a given graph by its ID.
 *
 * @param graph - The root graph to search within.
 * @param graphId - The ID of the subgraph to find. If undefined, the function returns undefined.
 * @param depth - The current depth of the recursion. Defaults to 0.
 * @param maxDepth - The maximum allowed depth for the recursion to prevent infinite loops. Defaults to 1000.
 * @returns The subgraph with the specified ID if found, otherwise undefined.
 * @throws Will throw an error if the maximum recursion depth is exceeded.
 */
const findGraphInGraph = (
    graph: ResourceGraph,
    graphId: ResourceId | undefined,
    depth = 0,
    maxDepth = 1000,
): ResourceGraph | undefined => {
    if (depth >= maxDepth) throw new Error("Flatten graph max depth exceeded");

    if (!graphId) return undefined;

    if (graph.resourceGroup?._id === graphId) return graph;

    const graphs = graph.children.filter(isResourceGraph);
    const found = graphs.find(graph =>
        findGraphInGraph(graph, graphId, depth + 1),
    );
    return found;
};

/**
 * Recursively searches for the parent graph of a given child resource within a resource graph.
 *
 * @param graph - The resource graph to search within.
 * @param childId - The ID of the child resource to find the parent graph for.
 * @param depth - The current depth of the search. Defaults to 0.
 * @param maxDepth - The maximum depth to search. Defaults to 1000.
 * @returns The parent resource graph containing the child resource, or `undefined` if not found.
 * @throws Will throw an error if the maximum search depth is exceeded.
 */
const findParentGraphOfChildInGraph = (
    graph: ResourceGraph,
    childId: ResourceId | undefined,
    depth = 0,
    maxDepth = 1000,
): ResourceGraph | undefined => {
    if (depth >= maxDepth) throw new Error("Flatten graph max depth exceeded");

    if (!childId) return undefined;

    if (
        graph.children
            .filter(r => !isResourceGraph(r))
            .find(child => child._id === childId)
    )
        return graph;
    const graphs = graph.children.filter(isResourceGraph);
    for (const graph of graphs) {
        const found = findParentGraphOfChildInGraph(graph, childId, depth + 1);
        if (found) return found;
    }

    return undefined;
};

/**
 * Flattens a hierarchical resource graph into a single array of resources, excluding resource groups.
 *
 * @param graph - The resource graph to flatten.
 * @param depth - The current depth of recursion, defaults to 0.
 * @param maxDepth - The maximum depth to flatten the graph, defaults to 1000.
 * @returns An array of resources excluding resource groups.
 * @throws Will throw an error if the maximum depth for flattening the graph is exceeded.
 */
const flattenGraph = (
    graph: ResourceGraph,
    depth = 0,
    maxDepth: number = 1000,
): Exclude<Resource, ResourceGroup>[] => {
    if (depth >= maxDepth) throw new Error("Flatten graph max depth exceeded");

    const graphs = graph.children.filter(isResourceGraph);
    const otherResources = graph.children.filter(isNotResourceGraph);
    const childrenResources = graphs.flatMap(graph =>
        flattenGraph(graph, depth + 1),
    );
    return [...otherResources, ...childrenResources];
};
type FlattenedResourceGraphItem = {
    parentIds: ResourceId[];
    resource: Resource;
};

/**
 * Flattens a hierarchical resource graph into a list of resources, keeping track of parent IDs.
 *
 * @param graph - The resource graph to flatten.
 * @param parentIds - An array of parent resource IDs. Defaults to an empty array.
 * @param depth - The current depth of recursion. Defaults to 0.
 * @param maxDepth - The maximum allowed depth of recursion. Defaults to 1000.
 * @returns An array of flattened resource graph items, each containing parent IDs and the resource.
 * @throws Will throw an error if the maximum depth is exceeded.
 */
const flattenGraphKeepParents = (
    graph: ResourceGraph,
    parentIds: ResourceId[] = [],
    depth = 0,
    maxDepth = 1000,
): FlattenedResourceGraphItem[] => {
    if (depth >= maxDepth) throw new Error("Flatten graph max depth exceeded");

    const currentParentIds = [
        ...parentIds,
        ...(graph.resourceGroup ? [graph.resourceGroup._id] : []),
    ];
    const resourceGraphs = graph.children.filter(isResourceGraph);
    const resources = graph.children
        .filter(isNotResourceGraph)
        .map(resource => {
            return {
                parentIds: currentParentIds,
                resource,
            };
        });
    const subResources = resourceGraphs.flatMap(resourceGraph => {
        return flattenGraphKeepParents(
            resourceGraph,
            currentParentIds,
            depth + 1,
        );
    });
    const all = [...resources, ...subResources];
    return all;
};

/**
 * Traverses a resource graph starting from the root graph and collects all subgraphs.
 *
 * @param rootGraph - The root resource graph to start the traversal from.
 * @returns An array of all resource graphs found during the traversal.
 */
const findAllGraphs = (rootGraph: ResourceGraph) => {
    const result = [];
    const stack = [rootGraph];

    while (stack.length > 0) {
        const currentGraph = stack.pop();
        if (!currentGraph) continue;
        result.push(currentGraph);
        const childrenGraphs = currentGraph.children.filter(isResourceGraph);
        stack.push(...childrenGraphs);
    }

    return result;
};

/**
 * Flattens a hierarchical resource graph into a single array while preserving resource groups.
 *
 * This function traverses the given root resource graph and collects all resource groups and
 * other resources into a flat array. It uses a depth-first search approach to ensure that
 * all nodes are visited.
 *
 * @param rootGraph - The root of the resource graph to be flattened.
 * @returns An array containing all resource groups and other resources from the graph.
 */
const flattenGraphKeepGroups = (rootGraph: ResourceGraph) => {
    const result = [];
    const stack = [rootGraph];

    while (stack.length > 0) {
        const currentGraph = stack.pop();
        if (!currentGraph) continue;
        result.push(currentGraph.resourceGroup);
        const childrenGraphs = currentGraph.children.filter(isResourceGraph);
        const otherResources = currentGraph.children.filter(isNotResourceGraph);
        result.push(...otherResources);
        stack.push(...childrenGraphs);
    }

    return result;
};

/**
 * Represents an organized structure of a graph containing resources and resource groups.
 *
 * @typedef OrganizedGraph
 *
 * @property {Exclude<Resource, ResourceGroup>[]} resources - An array of resources excluding resource groups.
 * @property {ResourceGroup[]} resourceGroups - An array of resource groups.
 * @property {Resource[]} all - An array of all resources, both regular and resource groups.
 * @property {Object} parentsMapped - An object containing mappings of parent resources.
 * @property {Map<ResourceId, ResourceId[]>} parentsMapped.all - A map of all parent resource ids for a resource anywhere above the resource in the hierarchy
 * @property {Map<ResourceId, ResourceId[]>} parentsMapped.direct - A map of direct parent resource ids.
 * @property {Map<ResourceId, Resource>} mapped - A map of resources mapped by their IDs.
 * @property {Resource} root - The root resource of the graph.
 */
type OrganizedGraph = {
    resources: Exclude<Resource, ResourceGroup>[];
    resourceGroups: ResourceGroup[];
    all: Resource[];
    parentsMapped: {
        all: Map<ResourceId, ResourceId[]>;
        direct: Map<ResourceId, ResourceId[]>;
    };
    mapped: Map<ResourceId, Resource>;
    root: Resource;
};

/**
 * Recursively searches for paths to a specific resource ID within a resource graph.
 *
 * @param root - The root of the resource graph to search within.
 * @param resourceId - The ID of the resource to find paths to.
 * @param depth - The current depth of the search (default is 0).
 * @param maxDepth - The maximum depth to search (default is 1000).
 * @returns An array of paths, where each path is an array of resource IDs leading to the target resource ID.
 */
const findPathsToResourceIdInGraph = (
    root: ResourceGraph,
    resourceId: ResourceId,
    depth = 0,
    maxDepth = 1000,
): ResourceId[][] => {
    const used = new Set<ResourceId>([root.resourceGroup._id]);
    const pathsFound: ResourceId[][] = [];
    const path: ResourceId[] = [root.resourceGroup._id];

    // first check children at the current level
    // then check children of children
    const directChildren = root.children.filter(isNotResourceGraph);
    const childGraphs = root.children.filter(isResourceGraph);

    for (const child of directChildren) {
        // Is this the resource we are looking for?
        if (child._id === resourceId) {
            path.push(child._id);
            pathsFound.push(path);
        }
    }

    // Check children of child graphs
    for (const graph of childGraphs) {
        if (used.has(graph._id)) continue; // circular reference, already checked
        used.add(graph._id);

        // Check child graph
        const paths = findPathsToResourceIdInGraph(
            graph,
            resourceId,
            depth + 1,
            maxDepth - 1,
        );
        if (paths.length) {
            paths.forEach(p => pathsFound.push([...path, ...p]));
        }
    }

    // If we reach this point, we've searched
    // all children and their children
    return pathsFound;
};

/**
 * Organizes a given resource graph into a structured format.
 *
 * @param graph - The resource graph to be organized.
 * @returns An object containing organized resources, resource groups, mappings, and parent relationships.
 *
 * The returned object includes:
 * - `resources`: An array of resources excluding resource groups.
 * - `resourceGroups`: An array of resource groups.
 * - `mapped`: A map of resource IDs to resources.
 * - `parentsMapped`: An object containing:
 *   - `all`: A map of resource IDs to all parent resource IDs (both direct and indirect).
 *   - `direct`: A map of resource IDs to direct parent resource IDs.
 * - `all`: An array of all resources including resource groups.
 * - `root`: The root resource group of the graph.
 */
const organizeGraph = (graph: ResourceGraph): OrganizedGraph => {
    const all = flattenGraphKeepGroups(graph);
    const resources = all.filter(isNotResourceGroup);
    const resourceGroups = all.filter(isResourceGroup);
    const mapped = new Map<ResourceId, Resource>();
    for (const resource of all) {
        mapped.set(resource._id, resource);
    }
    const directParentsMapped = new Map<ResourceId, ResourceId[]>();
    const allParentsMapped = new Map<ResourceId, ResourceId[]>();

    // Find all parents for each resource
    // both direct and indirect parents and store them in maps
    for (const resource of all) {
        const paths = findPathsToResourceIdInGraph(graph, resource._id);
        // Get a list of direct parents of the resource
        const directParents = paths
            .map(p => {
                if (p.length < 2) return undefined;
                return p[p.length - 2];
            })
            .filter(exists);
        directParentsMapped.set(resource._id, unique(directParents));
        // And then get a list of all possible parents never mind how deep they are
        // loop through all paths to the resource and add all parents to the set but discard the resource itself
        const allParents = paths
            .flatMap(p => p.slice(0, p.length - 1))
            .filter(p => p !== resource._id);
        allParentsMapped.set(resource._id, unique(allParents));
    }

    return {
        resources,
        resourceGroups,
        mapped,
        parentsMapped: {
            all: allParentsMapped,
            direct: directParentsMapped,
        },
        all,
        root: graph.resourceGroup,
    } satisfies OrganizedGraph;
};

const resourceGraphHelpers = {
    findGraphInGraph,
    findParentGraphOfChildInGraph,
    flattenGraph,
    flattenGraphKeepGroups,
    flattenGraphKeepParents,
    findAllGraphs,
    organizeGraph,
    findPathsToResourceIdInGraph,
};

export {
    findAllGraphs,
    findGraphInGraph,
    findParentGraphOfChildInGraph,
    findPathsToResourceIdInGraph,
    flattenGraph,
    flattenGraphKeepGroups,
    flattenGraphKeepParents,
    organizeGraph,
    resourceGraphHelpers,
};
export type { FlattenedResourceGraphItem, OrganizedGraph };
